import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { CallFlowsSideNavComponent, UiCallFlow } from './call-flows-side-nav/call-flows-side-nav.component';
import { TeamsAutoAttendantsCallFlowEditComponent } from './auto-attendants-call-flow-edit/teams-auto-attendants-call-flow-edit.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CallFlowGreetingsEntity,
  CallFlowGreetingsOptions,
} from './call-flow-greetings/call-flow-greetings-form.component';
import { TeamsAutoAttendantEditContext } from '../../teams-auto-attendant-edit.context';
import { cloneDeep } from 'lodash';
import { Observable, of } from 'rxjs';
import { ButtonSizes, ButtonStyles, ZiroButtonComponent } from '../../../button/button.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import {
  AutoAttendantHoliday,
  CallRoutingOption,
  MicrosoftTeamsAutoAttendantReferenceView,
  ReferenceType,
} from '../../../shared/models/generated/ziroModels';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ZiroFormsModule } from '../../../forms/ziro-forms.module';

export interface CallFlowGreetingUpdate {
  flowId: string;
  greeting: CallFlowGreetingsEntity;
}

@Component({
  selector: 'ziro-teams-auto-attendants-call-flow',
  templateUrl: './teams-auto-attendants-call-flow.component.html',
  styleUrl: './teams-auto-attendants-call-flow.component.scss',
  imports: [
    CallFlowsSideNavComponent,
    TeamsAutoAttendantsCallFlowEditComponent,
    TranslateModule,
    NgxSkeletonLoaderModule,
    ZiroButtonComponent,
    ZiroFormsModule,
  ],
})
export class TeamsAutoAttendantsCallFlowComponent implements OnInit {
  @ViewChildren(TeamsAutoAttendantsCallFlowEditComponent)
  callFlowEditComponents!: QueryList<TeamsAutoAttendantsCallFlowEditComponent>;

  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _translateService = inject(TranslateService);
  private _ziroModalService = inject(ZiroModalService);
  private _router = inject(Router);

  private readonly _referenceTypeConfig: Record<ReferenceType, { icon: ZiroIcons; label: string; path: string }> = {
    [ReferenceType.AUTO_ATTENDANT]: {
      icon: ZiroIcons.AUTO_ATTENDANT,
      label: 'Auto Attendant',
      path: 'teams-auto-attendants',
    },
    [ReferenceType.CALL_QUEUE]: { icon: ZiroIcons.CALL_QUEUE, label: 'Call Queue', path: 'teams-call-queues' },
  };

  // inputs
  standardCallFlows = input.required<UiCallFlow[]>();
  holidayCallFlows = input<UiCallFlow[]>();
  selectedCallFlow = signal<UiCallFlow | null>(null);
  tenantId = input.required<string>();
  references = input.required<MicrosoftTeamsAutoAttendantReferenceView[]>();
  isLoadingReferences = input.required<boolean>();
  allHolidays = input.required<AutoAttendantHoliday[]>();
  isDisabled = input<boolean>();

  // outputs
  greetingUpdate = output<CallFlowGreetingUpdate>();

  flowsWithErrors = signal<Set<string>>(new Set());
  private _validatedFlowIds = new Set<string>();

  get localHolidayCallFlows(): WritableSignal<UiCallFlow[]> {
    return this._editContext.localHolidayCallFlows;
  }

  readonly allCallFlows = computed(() => [...this.standardCallFlows(), ...this.localHolidayCallFlows()]);

  constructor() {
    effect(() => {
      this._editContext.localHolidayCallFlows();
      queueMicrotask(() => this._revalidateAllHolidayForms());
    });
  }

  ngOnInit(): void {
    const firstFlow = this.standardCallFlows()[0] ?? this._editContext.localHolidayCallFlows()[0] ?? null;
    this.selectedCallFlow.set(firstFlow);
  }

  onCallFlowSelected(flow: UiCallFlow): void {
    const current = this.selectedCallFlow();
    if (current) {
      const currentEditComponent = this.callFlowEditComponents.find((c) => c.callFlow().id === current.id);
      const greetingEntity = currentEditComponent?.greetingsForm?.entity;
      if (greetingEntity) {
        this._editContext.updateGreeting(
          current.id,
          greetingEntity.greeting ?? null,
          greetingEntity.selectedGreetingOption,
        );
      }
      this._validatedFlowIds.add(current.id);
      currentEditComponent?.validateAndShowErrors();
      this._refreshFlowError(current.id, currentEditComponent?.isValid() ?? true);
    }
    this.selectedCallFlow.set(flow);
  }

  navigateToReference(ref: MicrosoftTeamsAutoAttendantReferenceView): void {
    const { path } = this._referenceTypeConfig[ref.type];
    this._router.navigate(['/auto-attendant-management/tenants', this.tenantId(), path, ref.id]);
  }

  getReferenceIcon(type: ReferenceType): ZiroIcons {
    return this._referenceTypeConfig[type].icon;
  }

  getReferenceTypeLabel(type: ReferenceType): string {
    return this._referenceTypeConfig[type].label;
  }

  getReferenceHref(ref: MicrosoftTeamsAutoAttendantReferenceView): string {
    return `/auto-attendant-management/tenants/${this.tenantId()}/${this._referenceTypeConfig[ref.type].path}/${ref.id}`;
  }

  onDuplicateHolidayCallFlow(flow: UiCallFlow): void {
    const callFlowCopy = cloneDeep(flow);
    const newCallFlow: UiCallFlow = {
      ...callFlowCopy,
      id: crypto.randomUUID(),
      name: `${callFlowCopy.name} (copy)`,
      isHoliday: true,
      isAfterHoursCallFlow: false,
      holiday: {
        id: null,
        name: '',
        dateTimeRanges: [],
      },
    };
    delete newCallFlow.schedule;
    this._editContext.addHolidayCallFlow(newCallFlow);
    this.selectedCallFlow.set(newCallFlow);
    this._validatedFlowIds.add(newCallFlow.id);
    setTimeout(() => this._forceValidateAllFlows());
  }

  onDeleteHolidayCallFlow(flow: UiCallFlow): void {
    const options = {
      modalViewProperties: {
        promptBody: this._translateService.instant(
          'tkey;admin.teams_voice_management.teams_auto_attendant.delete_holiday.confirmation.modal.prompt',
          {
            name: flow.name,
          },
        ),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._removeHolidayFlow(flow),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  onAddHolidayCallFlow(): void {
    const newFlow = {
      id: crypto.randomUUID(),
      name: this._translateService.instant(
        'tkey;admin.teams_voice_management.teams_auto_attendant.new_holiday_call_flow.default_name',
      ),
      isHoliday: true,
      holiday: {
        id: crypto.randomUUID(),
        name: '',
        dateTimeRanges: [],
      },
      schedule: null,
      callRoutingOption: CallRoutingOption.DISCONNECT, // This needs to be updated when we modify Destinations,
      selectedGreetingOption: CallFlowGreetingsOptions.NONE,
    } as UiCallFlow;

    this._editContext.addHolidayCallFlow(newFlow);
    this.selectedCallFlow.set(newFlow);
    this._validatedFlowIds.add(newFlow.id);
    setTimeout(() => this._forceValidateAllFlows());
  }

  onHolidayCallFlowNameChange(flowId: string, newName: string): void {
    this._editContext.updateHolidayCallFlowName(flowId, newName);
    if (this.selectedCallFlow()?.id === flowId) {
      this.selectedCallFlow.update((cf) => ({ ...cf, name: newName }));
    }
  }

  onGreetingUpdate(flowId: string, greeting: CallFlowGreetingsEntity): void {
    this._editContext.updateGreeting(
      flowId,
      greeting.greeting,
      greeting.selectedGreetingOption,
      greeting?.isDestinationsGreeting,
    );
    this.greetingUpdate.emit({ flowId, greeting });
  }

  onNavigateToBusinessHours(): void {
    const businessHoursFlow = this.standardCallFlows().find((f) => !f.isAfterHoursCallFlow && !f.isHoliday);
    if (businessHoursFlow) {
      this.onCallFlowSelected(businessHoursFlow);
    }
  }

  areCallFlowComponentsValid(): boolean {
    let allValid = true;

    this.callFlowEditComponents.forEach((component) => {
      const flowId = component.callFlow().id;
      this._validatedFlowIds.add(flowId);
      const valid = component.validateAndShowErrors();
      this._refreshFlowError(flowId, valid);
      if (!valid) {
        allValid = false;
      }
    });
    return allValid;
  }

  onFlowValidityChange(flowId: string, isValid: boolean): void {
    if (!this._validatedFlowIds.has(flowId)) {
      return;
    }
    this._refreshFlowError(flowId, isValid);
  }

  private _revalidateAllHolidayForms(): void {
    this.callFlowEditComponents?.forEach((component) => {
      if (component.callFlow().isHoliday) {
        component.holidayForm?.validateAllFields();
      }
    });
  }

  private _forceValidateAllFlows(): void {
    this.callFlowEditComponents?.forEach((component) => {
      const flowId = component.callFlow().id;
      this._validatedFlowIds.add(flowId);
      component.validateAndShowErrors();
    });
    setTimeout(() => {
      this.callFlowEditComponents?.forEach((component) => {
        this._refreshFlowError(component.callFlow().id, component.isValid());
      });
    });
  }

  private _refreshFlowError(flowId: string, isValid: boolean): void {
    this.flowsWithErrors.update((prev) => {
      const next = new Set(prev);
      if (isValid) {
        next.delete(flowId);
      } else {
        next.add(flowId);
      }
      return next;
    });
  }

  private _removeHolidayFlow(flow: UiCallFlow): Observable<void> {
    this._editContext.removeHolidayCallFlow(flow.id);
    this._validatedFlowIds.delete(flow.id);
    this.flowsWithErrors.update((prev) => {
      const next = new Set(prev);
      next.delete(flow.id);
      return next;
    });

    if (this.selectedCallFlow()?.id === flow.id) {
      const remaining =
        this.localHolidayCallFlows()[this.localHolidayCallFlows().length - 1] ?? this.standardCallFlows()[0] ?? null;
      this.selectedCallFlow.set(remaining);
    }

    return of(null);
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly ButtonSizes = ButtonSizes;
}
